import { useEffect, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { MaterialListItem } from '@kreart/shared';
import { edgesApi } from '@/features/catalog/components/admin/api';
import { chooseEdgeForMaterial } from './edgeAutoSelect';

/**
 * Resolve the edge that goes with a material, keyed off the material's
 * manufacturer.
 *
 * This is the behaviour the Custom cabinet tab has always had: on picking a
 * material, search the edge catalogue for that manufacturer and let
 * `chooseEdgeForMaterial` rank the hits. Extracted so the cut-list rows resolve
 * their edge identically instead of re-implementing it — the two would
 * otherwise drift the moment the ranking rules change.
 *
 * `onResolved` fires with the chosen edge id, or `''` when the material takes no
 * banding (MDF_VOPSIT is painted) or has no manufacturer to search by.
 *
 * Only reacts to a *changed material* — it deliberately does nothing on mount,
 * so hydrating a saved cabinet never overwrites the edges already stored on it.
 */
export function useAutoSelectedEdge({
  material,
  enabled = true,
  preferLargestProduct = false,
  onResolved,
}: {
  material: MaterialListItem | null;
  enabled?: boolean;
  /** Thicker board (Cx2 / a doubled piece) wants the larger edge. */
  preferLargestProduct?: boolean;
  onResolved: (edgeId: string) => void;
}): void {
  const search = material?.manufacturer?.trim() ?? '';
  const searchable = Boolean(material) && material?.category !== 'MDF_VOPSIT' && Boolean(search);

  const query = useQuery({
    queryKey: ['catalog-edges', 'auto-select', search],
    queryFn: () =>
      edgesApi
        .list({ page: 1, pageSize: 100, active: true, includeDeleted: false, search })
        .then((res) => res.items),
    enabled: enabled && searchable,
    meta: { silent: true },
    staleTime: 60_000,
  });

  // Callers pass an inline arrow; keeping it in a ref stops the effect from
  // re-running (and re-writing the field) on every render.
  const onResolvedRef = useRef(onResolved);
  onResolvedRef.current = onResolved;

  useEffect(() => {
    if (!enabled || !material) return;
    if (!searchable) {
      onResolvedRef.current('');
      return;
    }
    if (!query.data) return;
    const edge = chooseEdgeForMaterial(material, query.data, { preferLargestProduct });
    onResolvedRef.current(edge?.id ?? '');
  }, [enabled, material, searchable, query.data, preferLargestProduct]);
}
